// The maker workflow on the API: briefs, assignments, deliveries, the producer overview, and the member
// scope settings. Types follow the API's snake_case responses. The pure helpers at the bottom (grouping,
// money, deadlines, criteria) are what the views are built from, and what the tests cover.
import type { Api } from "./api.ts";

export type AssignmentState =
  | "offered"
  | "accepted"
  | "declined"
  | "in_progress"
  | "delivered"
  | "changes_requested"
  | "approved"
  | "paid"
  | "cancelled";

export interface BonusRule {
  type: "batch";
  min_items?: number;
  pct?: number;
  amount?: number;
}

export interface Brief {
  id: string;
  project_id: string;
  title: string;
  description: string | null;
  criteria: unknown[];
  shot_ids: string[];
  episode_ids: string[];
  fee: number | null;
  currency: string;
  deadline: string | null;
  bonus_rule: BonusRule | null;
  created_at: string;
  archived_at: string | null;
}
export interface BriefWithCount extends Brief {
  assignment_count: number;
}

export interface Assignment {
  id: string;
  project_id: string;
  episode_id: string | null;
  brief_id: string | null;
  maker_id: string;
  status: AssignmentState;
  fee: number | null;
  currency: string;
  deadline: string | null;
  batch_id: string | null;
  bonus_rule: BonusRule | null;
  note: string | null;
  offered_by: string | null;
  accepted_at: string | null;
  paid_at: string | null;
  payment_ref: string | null;
  created_at: string;
  updated_at: string;
}
export interface AssignmentListItem extends Assignment {
  brief_title: string | null;
  waiting_delivery_id: string | null;
}

export interface Delivery {
  id: string;
  assignment_id: string;
  take_ids: string[];
  asset_version_ids: string[];
  note: string | null;
  submitted_by: string | null;
  submitted_at: string;
  decision: "approved" | "changes_requested" | null;
  decision_note: string | null;
  decided_by: string | null;
  decided_at: string | null;
}

export interface MakerCost {
  id: string;
  amount: number;
  currency: string;
  unit_kind: string | null;
  occurred_at: string;
  paid_at: string | null;
}

export interface AssignmentView extends Assignment {
  brief: Brief | null;
  deliveries: Delivery[];
  costs: MakerCost[];
  maker: { id: string; email: string | null; display_name: string | null } | null;
}

export interface MakersOverview {
  by_status: Record<string, number>;
  awaiting_review: {
    assignment_id: string;
    delivery_id: string;
    maker_id: string;
    brief_title: string | null;
    submitted_at: string;
  }[];
  owed: Record<string, number>;
  paid: Record<string, number>;
}

export interface DecisionResult {
  delivery: Delivery;
  assignment: Assignment;
  costs: MakerCost[];
  decided: boolean;
}

export interface BriefInput {
  projectId?: string;
  title?: string;
  description?: string | null;
  criteria?: string[];
  shotIds?: string[];
  episodeIds?: string[];
  fee?: number | null;
  currency?: string;
  deadline?: string | null;
  bonusRule?: BonusRule | null;
}

export interface Member {
  workspace_id: string;
  user_id: string;
  role: string;
  project_scope: string[] | null;
  email: string | null;
  display_name: string | null;
}
export interface InvitationRow {
  id: string;
  email: string;
  role: string;
  project_scope: string[] | null;
  expires_at: string;
  accepted_at: string | null;
}

/** Everything the maker workflow and member settings ask of the server. */
export function makersApi(api: Api, ws: string) {
  const w = `/workspaces/${ws}`;
  const get = <T>(path: string, query?: Record<string, string | undefined>) =>
    api.request<T>("GET", `${w}${path}`, { query });
  const post = <T>(path: string, body: unknown = {}) => api.request<T>("POST", `${w}${path}`, { body });
  return {
    overview: (projectId?: string) => get<MakersOverview>("/makers/overview", { projectId }),
    listAssignments: (q: { projectId?: string; mine?: boolean; status?: string } = {}) =>
      get<{ items: AssignmentListItem[] }>("/assignments", {
        projectId: q.projectId,
        mine: q.mine ? "true" : undefined,
        status: q.status,
      }).then((r) => r.items),
    getAssignment: (id: string) => get<AssignmentView>(`/assignments/${id}`),
    listBriefs: (projectId?: string) =>
      get<{ items: BriefWithCount[] }>("/briefs", { projectId }).then((r) => r.items),
    getBrief: (id: string) => get<Brief>(`/briefs/${id}`),
    createBrief: (body: BriefInput & { projectId: string; title: string }) => post<Brief>("/briefs", body),
    updateBrief: (id: string, body: BriefInput) => api.request<Brief>("PATCH", `${w}/briefs/${id}`, { body }),
    archiveBrief: (id: string) => post<Brief>(`/briefs/${id}/archive`),
    offer: (
      briefId: string,
      body: { makerId: string; fee?: number; deadline?: string; note?: string; episodeId?: string },
    ) => post<Assignment>(`/briefs/${briefId}/assignments`, body),
    move: (id: string, move: "accept" | "decline" | "start") =>
      post<Assignment>(`/assignments/${id}/${move}`),
    deliver: (id: string, body: { takeIds?: string[]; assetVersionIds?: string[]; note?: string }) =>
      post<{ delivery: Delivery; assignment: Assignment }>(`/assignments/${id}/deliveries`, body),
    approve: (id: string, note?: string) => post<DecisionResult>(`/assignments/${id}/approve`, { note }),
    requestChanges: (id: string, note?: string) =>
      post<DecisionResult>(`/assignments/${id}/request-changes`, { note }),
    markPaid: (id: string, body: { idempotencyKey: string; costEntryIds: string[]; paymentRef?: string }) =>
      post<Assignment>(`/assignments/${id}/mark-paid`, body),
    cancel: (id: string, note?: string) => post<Assignment>(`/assignments/${id}/cancel`, { note }),
    // members and access
    members: () => get<{ items: Member[] }>("/members", { limit: "200" }).then((r) => r.items),
    invitations: () => get<{ items: InvitationRow[] }>("/invitations", { limit: "200" }).then((r) => r.items),
    setRole: (userId: string, role: string) =>
      api.request<Member>("PATCH", `${w}/members/${userId}`, { body: { role } }),
    setScope: (userId: string, projectScope: string[] | null) =>
      api.request<Member>("PUT", `${w}/members/${userId}/scope`, { body: { projectScope } }),
    invite: (body: { email: string; role: string; projectScope?: string[] | null }) =>
      post<InvitationRow & { token: string }>("/invitations", body),
  };
}
export type MakersApi = ReturnType<typeof makersApi>;

// ─── pure helpers ────────────────────────────────────────────────────────────

/** The lifecycle in order. Declined and cancelled close the story early. */
export const STATE_ORDER: readonly AssignmentState[] = [
  "offered",
  "accepted",
  "in_progress",
  "delivered",
  "changes_requested",
  "approved",
  "paid",
  "declined",
  "cancelled",
];

export const STATE_LABEL: Record<AssignmentState, string> = {
  offered: "Offered",
  accepted: "Accepted",
  in_progress: "In progress",
  delivered: "Delivered",
  changes_requested: "Changes requested",
  approved: "Signed off",
  paid: "Paid",
  declined: "Declined",
  cancelled: "Cancelled",
};

/** What a state means to the person looking at it, in one line. */
export function stateHint(s: AssignmentState, as: "producer" | "maker"): string {
  const p = as === "producer";
  switch (s) {
    case "offered":
      return p ? "Waiting for the maker to accept." : "Accept or decline this brief.";
    case "accepted":
      return p ? "Accepted. The maker has not started yet." : "Accepted. Start when you begin working.";
    case "in_progress":
      return p
        ? "The maker is working on it."
        : "Make takes in the assigned shots, then submit your delivery.";
    case "delivered":
      return p ? "Waiting for your review." : "Delivered. The producer is reviewing it.";
    case "changes_requested":
      return p ? "Waiting for a new delivery." : "Changes were requested. Deliver again when they are done.";
    case "approved":
      return p
        ? "Signed off. The fee is booked; mark it paid when you have paid."
        : "Signed off. Payment is next.";
    case "paid":
      return "Paid.";
    case "declined":
      return "The maker declined this brief.";
    case "cancelled":
      return "Cancelled.";
  }
}

/**
 * Dot colour class of a state: status shows only as a small dot and a word, never a fill or the accent.
 * Lifecycle steps (offered, accepted, in progress, delivered, declined, cancelled) are a neutral dot.
 */
export function stateTone(s: AssignmentState): "ok" | "warn" | "idle" {
  switch (s) {
    case "approved":
    case "paid":
      return "ok";
    case "changes_requested":
      return "warn";
    default:
      return "idle";
  }
}

export interface StateGroup<T> {
  state: AssignmentState;
  items: T[];
}

/** Assignments grouped by state in lifecycle order; empty states are left out. */
export function groupByState<T extends { status: AssignmentState }>(items: readonly T[]): StateGroup<T>[] {
  const out: StateGroup<T>[] = [];
  for (const state of STATE_ORDER) {
    const list = items.filter((i) => i.status === state);
    if (list.length) out.push({ state, items: list });
  }
  return out;
}

const SYMBOL: Record<string, string> = { USD: "$", EUR: "€", GBP: "£" };

export function formatMoney(amount: number | null | undefined, currency = "USD"): string {
  if (amount === null || amount === undefined || !Number.isFinite(amount)) return "–";
  const sym = SYMBOL[currency];
  const n = Math.abs(amount).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${amount < 0 ? "-" : ""}${sym ?? ""}${n}${sym ? "" : ` ${currency}`}`;
}

/** "$150.00 + €40.00": one amount per currency, largest first; "–" when nothing is owed. */
export function formatTotals(totals: Record<string, number> | undefined): string {
  const entries = Object.entries(totals ?? {}).filter(([, v]) => v > 0);
  if (!entries.length) return "–";
  return entries
    .sort((a, b) => b[1] - a[1])
    .map(([c, v]) => formatMoney(v, c))
    .join(" + ");
}

/**
 * A date-only deadline in the viewer's words: `text` reads "Due 29 Oct" (the year only when it is not this
 * year), `date` is the same day without the "Due"; overdue is flagged.
 */
export function deadlineText(
  iso: string | null | undefined,
  now = Date.now(),
): { text: string; date: string; overdue: boolean } {
  const none = { text: "No deadline", date: "No deadline", overdue: false };
  if (!iso) return none;
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return none;
  // A deadline is a calendar day (stored as the end of that day in UTC): show that day, not the viewer's.
  const day = new Date(t);
  const sameYear = day.getUTCFullYear() === new Date(now).getUTCFullYear();
  const date = day.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    ...(sameYear ? {} : { year: "numeric" }),
    timeZone: "UTC",
  });
  return { text: `Due ${date}`, date, overdue: t < now };
}

/** `<input type=date>` value (yyyy-mm-dd) → ISO at the end of that day (UTC); empty → null. */
export function dateInputToIso(v: string): string | null {
  return /^\d{4}-\d{2}-\d{2}$/.test(v) ? `${v}T23:59:59.000Z` : null;
}
export const isoToDateInput = (iso: string | null | undefined): string => (iso ? iso.slice(0, 10) : "");

/** Acceptance criteria are stored as strings (or objects with a text); show and edit them as lines. */
export function criteriaToLines(criteria: readonly unknown[] | null | undefined): string[] {
  const out: string[] = [];
  for (const c of criteria ?? []) {
    if (typeof c === "string") out.push(c);
    else if (c && typeof c === "object") {
      const t = (c as { text?: unknown; title?: unknown }).text ?? (c as { title?: unknown }).title;
      if (typeof t === "string") out.push(t);
    }
  }
  return out;
}
export const linesToCriteria = (text: string): string[] =>
  text
    .split("\n")
    .map((l) => l.replace(/^\s*(?:[-*•]|\d+[.)])\s*/, "").trim())
    .filter(Boolean);

/** A fee field → a non-negative number, or null when empty; NaN when it is not a number. */
export function parseFee(v: string): number | null {
  const s = v.trim().replace(",", ".");
  if (!s) return null;
  const n = Number(s);
  return Number.isFinite(n) && n >= 0 ? Math.round(n * 100) / 100 : Number.NaN;
}

/** "Bonus: +10% when 3 of the batch are approved", from a batch rule. */
export function bonusText(rule: BonusRule | null | undefined, currency = "USD"): string | null {
  if (!rule) return null;
  const what = rule.pct ? `+${rule.pct}%` : rule.amount ? `+${formatMoney(rule.amount, currency)}` : null;
  if (!what) return null;
  return `${what} each when ${rule.min_items ?? 1} of the batch are signed off`;
}

/** The name to show for a person. */
export const personName = (p: { display_name?: string | null; email?: string | null } | null | undefined) =>
  p?.display_name?.trim() || p?.email || "Someone";

/** Which scope a member has, in words: all projects, none, or N named ones. */
export function scopeText(scope: string[] | null | undefined, projectName: (id: string) => string): string {
  if (scope === null || scope === undefined) return "All productions";
  if (scope.length === 0) return "Only what they are assigned";
  return scope.map(projectName).join(", ");
}

/** Roles whose members always see every project, so a scope would do nothing. */
export const ALWAYS_UNSCOPED = new Set(["owner", "producer", "director"]);
